import axios from 'axios';

import getLogger from '../lib/logger';
import { getRequestId } from '../lib/request-id';
import authErrorHandler from '../util/authErrorHandler';
import getJwtToken from '../util/getJwtToken';

import { API_BASE_URL } from './config';
import { auth } from './firebase';

const logger = getLogger();

axios.defaults.withCredentials = true;

const getSafeErrorContext = (error) => ({
  name: error?.name || '',
  message: error?.message || '',
  stack: error?.stack || '',
  status: error?.response?.status || '',
});

// reusable function to make a request to our APIs
const api = async ({ url, payload, method = 'post', token = null }, cancelToken = null) => {
  try {
    const firebaseToken = token || (await auth?.currentUser?.getIdToken());
    if (!firebaseToken) {
      return { success: false, message: '' };
    }
    const retrieveResults = await axios({
      method,
      mode: 'no-cors',
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'X-Request-Id': getRequestId(),
        ...getJwtToken(),
      },
      url: API_BASE_URL + url,
      data: payload,
      ...(method === 'get' && { params: payload }),
      ...(cancelToken && { cancelToken }),
    });
    if (retrieveResults?.data && retrieveResults?.data?.success === false) {
      return { success: false, message: retrieveResults?.data?.message };
    }
    return { success: true, data: retrieveResults?.data?.data || retrieveResults?.data?.message || retrieveResults?.data };
  } catch (error) {
    if (error.response && error.response.data) {
      await authErrorHandler(error);
      return {
        success: false,
        message: error.response.data?.error || error.response.data?.message || error.response.data,
        // errorData, not data: legacy callers destructure data without checking success
        errorData: error.response.data,
      };
    }
    logger.error('Error in api', {
      function: 'api',
      error: getSafeErrorContext(error),
      url,
      method,
    });
    return { success: false, message: error.message };
  }
};

// Helper function for blob downloads (CSV, Excel, PDF, etc.)
export const apiBlobDownload = async ({ url, payload, method = 'post', token = null, baseUrl = API_BASE_URL }, cancelToken = null) => {
  const firebaseToken = token || (await auth?.currentUser?.getIdToken());

  if (!firebaseToken) {
    throw new Error('Authentication required');
  }

  try {
    const response = await axios({
      url: baseUrl + url,
      method,
      responseType: 'blob',
      data: payload,
      headers: {
        Authorization: `Bearer ${firebaseToken}`,
        'X-Request-Id': getRequestId(),
        ...getJwtToken(),
      },
      ...(cancelToken && { cancelToken }),
    });

    // Define valid file types for downloads
    const validFileTypes = [
      'text/csv',
      'application/pdf',
      'text/plain',
      'application/octet-stream', // Generic binary
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // needed for dashboard downloads
    ];

    // Check if the response is an error disguised as a blob
    // If it's not a valid file type, it's likely an error response
    if (response.data && response.data.type && !validFileTypes.includes(response.data.type)) {
      // Try to read the blob as text to get error message
      const text = await response.data.text();
      let errorMessage = 'An error occurred during file download';

      try {
        const errorData = JSON.parse(text);
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If it's not JSON, use the text as is (might be plain text error)
        errorMessage = text || errorMessage;
      }

      throw new Error(errorMessage);
    }

    return response.data;
  } catch (error) {
    // Handle axios errors (4xx, 5xx status codes)
    if (error.response && error.response.data) {
      // Try to read the error response body (which is a blob)
      const text = await error.response.data.text();
      let errorMessage = `Request failed with status code ${error.response.status}`;

      try {
        const errorData = JSON.parse(text);
        errorMessage = errorData.message || errorMessage;
      } catch {
        // If it's not JSON, use the text as is
        errorMessage = text || errorMessage;
      }

      throw new Error(errorMessage);
    }

    // Re-throw other errors (network issues, etc.)
    throw error;
  }
};

export default api;
